import React from 'react';
import * as _ from 'lodash';
import Helmet from 'react-helmet';
import Joi from 'joi-browser';

import SessionStorageService from '../services/utilities/SessionStorageService';
import { validationByJoi } from '../utilities/commonValidation';
import masterSlideLoader from '../utilities/masterSlideLoader';
import { messageUtility } from '../utilities/messageUtility';
import { MasterPageHeader } from '../components/PageHeader';
import CompanyService from '../services/Companies';
import { WorkerRegistrationApplicationService } from '../services/WorkerRegistrationApplication';
import { approvalFileType, fileSizeCheck } from '../utilities/fileType';
import { CloseEditView } from '../components/CloseEditView';
import dialogLoader from '../utilities/dialogLoader';
import { FormInformationDialog } from '../components/FormInformationDialog';
import { WorkerRegistrationApplicationInstructions } from '../components/WorkerRegistrationApplicationInstructions';
import { WorkerRegistrationApplicationUploadInfo } from '../components/WorkerRegistrationApplicationUploadInfo';
import { WorkerRegistrationApplicationFormUpload } from '../components/forms/WorkerRegistrationApplicationFormUpload';
import { WorkerRegistrationApplicationConfirmModal } from '../components/modals/WorkerRegistrationApplicationConfirmModal';
import { CcusErrorDialog } from '../components/modals/CcusErrorDialog';
import AuthProvider from '../utilities/authProvider';
import '../styles/pages/worker-registration-application.scss';

const ACCEPTED_FILE_TYPES = ['pdf'];
const MAX_FILE_SIZE = 10485760;
const STATUS_PROCESSING = [0, 1, 2, 3, 4];

export class WorkerRegistrationApplication extends React.Component {
  constructor(props) {
    super(props);

    this.selectedPartnerCompanyId = SessionStorageService.getSessionSelectedPartnerCompanyId();
    this.partnerCompanyIdOfPrimeCompany = SessionStorageService.getSessionPartnerCompanyIdOfPrimeCompany();
    this.schema = {
      fileName: Joi.string()
        .max(250)
        .required(),
    };

    this.state = {
      selectedPartnerCompanyName: '',
      isOpenRegisterConfirmModal: false,
      uploadedFileInfo: {},
      selectedFile: null,
      formValues: {
        fileName: '',
      },
      errors: {},
      isUploading: false,
      isFetchingFileInfo: true,
    };
  }

  componentDidMount() {
    CompanyService.show(this.selectedPartnerCompanyId).then(response => {
      if (response) {
        const { data, result } = response;

        if (result) {
          this.setState({
            selectedPartnerCompanyName: data.entities.company.name,
          });
        }
      }
    });

    WorkerRegistrationApplicationService.getUploadedFileInfo({
      applied_company_id: this.partnerCompanyIdOfPrimeCompany,
      target_company_id: this.selectedPartnerCompanyId,
      status: STATUS_PROCESSING,
    }).then(response => {
      if (response.status === 403) {
        return window.location.replace('/error-permission');
      }

      if (response) {
        const { data, result } = response;

        if (result) {
          this.setState({
            uploadedFileInfo: data.entities.worker_registration_application[0],
          });
        }
        this.setState({
          isFetchingFileInfo: false,
        });
      }
    });
  }

  closePage = () => {
    dialogLoader.show(CloseEditView, {
      callback: {
        close: () => {
          dialogLoader.close();
        },
        clear: () => {
          dialogLoader.close();
          masterSlideLoader.show('Workers', { state: {} });
        },
      },
    });
  };

  getBackBtnText = () =>
    this.partnerCompanyIdOfPrimeCompany === this.selectedPartnerCompanyId
      ? '自社作業員一覧'
      : `${this.state.selectedPartnerCompanyName} 作業員一覧`;

  onFileChange = event => {
    const { files } = event.target;
    const { getMessage } = messageUtility;
    const formValues = { ...this.state.formValues };
    const errors = { ...this.state.errors };

    if (files && files[0]) {
      Promise.all([
        approvalFileType(files[0], ACCEPTED_FILE_TYPES),
        fileSizeCheck(files[0], MAX_FILE_SIZE),
      ]).then(([validType, validSize]) => {
        if (!validSize) {
          errors.fileName = getMessage('E-000-048', '10');
        } else if (!validType) {
          errors.fileName = getMessage('E-000-038', ACCEPTED_FILE_TYPES);
        } else {
          errors.fileName = '';
        }

        formValues.fileName = files[0].name;
        this.setState(
          { formValues, errors, selectedFile: files[0] },
          this.validate
        );
      });
    }
  };

  validate = onSuccess => {
    const result = validationByJoi(this.schema, this.state.formValues);
    const { fileName } = this.state.errors;

    if (fileName) {
      result.messages.fileName = fileName;
    }

    const errors = { ...this.state.errors, ...result.messages };

    if (_.isEmpty(_.omitBy(errors, item => !item)) && onSuccess) {
      onSuccess();
    }
    this.setState({ errors });
  };

  deleteFile = () => {
    const formValues = { ...this.state.formValues };
    const errors = { ...this.state.errors };
    formValues.fileName = '';
    errors.fileName = '';

    this.setState({ formValues, errors }, this.validate);
  };

  uploadFile = () => {
    if (this.state.isUploading) {
      return;
    }

    const { getMessage } = messageUtility;
    const formData = new FormData();

    formData.append(
      'applied_file',
      this.state.selectedFile,
      this.state.selectedFile.name
    );
    formData.append('applied_company_id', this.partnerCompanyIdOfPrimeCompany);
    formData.append('target_company_id', this.selectedPartnerCompanyId);

    this.setState({ isUploading: true });

    WorkerRegistrationApplicationService.uploadWorkerRegisterAppPDF(formData)
      .then(response => {
        if (response) {
          const { result, data } = response;

          if (
            result &&
            _.has(data, 'entities.worker_registration_application')
          ) {
            this.setState({
              uploadedFileInfo: data.entities.worker_registration_application,
              selectedFile: null,
              formValues: {},
            });
          }
        }
      })
      .catch(error => {
        const errorMessage = error.toString();

        if (errorMessage.includes('Network Error')) {
          return this.showErrorDialog(getMessage('E-000-051'));
        }

        switch (error.status) {
          case 400: {
            if (_.has(error, 'data.message') && error.data.message) {
              this.showErrorDialog(error.data.message);
            }
            return;
          }
          case 401: {
            return dialogLoader.show(
              CcusErrorDialog,
              {
                callback: {
                  done: () => {
                    AuthProvider.instance.logout();
                  },
                },
                message:
                  error.data.message || messageUtility.getMessage('E-000-036'),
              },
              null
            );
          }
          case 403:
          case 404: {
            window.location.replace('/error-permission');
            return;
          }
          case 422: {
            return;
          }
          case 503: {
            return window.location.replace('/');
          }
          default: {
            window.location.replace('/error-500');
          }
        }
      })
      .finally(() => {
        this.setState({ isUploading: false });
      });
  };

  resetFileInput = event => {
    event.target.value = null;
  };

  toggleRegisterConfirmModal = () => {
    this.setState({
      isOpenRegisterConfirmModal: !this.state.isOpenRegisterConfirmModal,
    });
  };

  showErrorDialog = message => {
    dialogLoader.show(FormInformationDialog, {
      title: 'エラーが発生しました',
      message,
      onClose: () => {
        dialogLoader.close();
      },
    });
  };

  render() {
    return (
      !this.state.isFetchingFileInfo && (
        <div
          id='slide-view'
          className='WorkerRegistrationApplication master-slide-view show'
        >
          <Helmet>
            <title>作業員代行登録申請</title>
          </Helmet>
          <div className='master-slide-view__view'>
            <MasterPageHeader
              title={`${
                this.state.selectedPartnerCompanyName
              } 作業員代行登録申請`}
              onClose={this.closePage}
            />
            <div className='master-slide-view__table workers-capture'>
              <div className='master-slide-view__back-btn'>
                <button className='btn-text--sm' onClick={this.closePage}>
                  <i className='i-back--blue i-2x vertical-middle' />
                  {this.getBackBtnText()}
                </button>
              </div>
              <div className='WorkerRegistrationApplication__content'>
                {!_.isEmpty(this.state.uploadedFileInfo) && (
                  <WorkerRegistrationApplicationUploadInfo
                    fileInfo={this.state.uploadedFileInfo}
                  />
                )}

                <div className='WorkerRegistrationApplication__content-register-btn-wrapper'>
                  <button
                    className={
                      _.isEmpty(this.state.uploadedFileInfo)
                        ? 'btn--effective'
                        : 'btn-fixedsize--sub btn-fixedsize--disabled'
                    }
                    onClick={this.toggleRegisterConfirmModal}
                    disabled={!_.isEmpty(this.state.uploadedFileInfo)}
                  >
                    申請する
                  </button>
                </div>

                <div className='WorkerRegistrationApplication__content-separator' />

                <WorkerRegistrationApplicationFormUpload
                  disabled={!_.isEmpty(this.state.uploadedFileInfo)}
                  formValues={this.state.formValues}
                  errors={this.state.errors}
                  onFileChange={this.onFileChange}
                  onFileDelete={this.deleteFile}
                  onFileClick={this.resetFileInput}
                />
                <div className='WorkerRegistrationApplication__content-instructions'>
                  <div className='WorkerRegistrationApplication__content-instructions__header'>
                    <h2 className='WorkerRegistrationApplication__content-instructions__header-heading'>
                      必ずご確認ください
                    </h2>
                  </div>
                  <div className='WorkerRegistrationApplication__content-instructions__info-text'>
                    <p>
                      以下の2社のシステムをご利用の場合のみ、そのシステムから出力した作業員名簿データ（PDF形式）を添付していただくことで、作業員情報の代行登録を申請することができます。
                    </p>
                    <p>
                      （現在、その他の作業員名簿データは対応しておりません。大変申し訳ございませんが、ご自身でBuildeeの画面上から登録作業を行ってください）
                    </p>
                  </div>
                  <WorkerRegistrationApplicationInstructions />
                </div>
              </div>
            </div>
          </div>

          <WorkerRegistrationApplicationConfirmModal
            visible={this.state.isOpenRegisterConfirmModal}
            selectedPartnerCompanyId={this.selectedPartnerCompanyId}
            partnerCompanyIdOfPrimeCompany={this.partnerCompanyIdOfPrimeCompany}
            onConfirm={() => {
              this.toggleRegisterConfirmModal();
              this.validate(this.uploadFile);
            }}
            onCancel={this.toggleRegisterConfirmModal}
          />
        </div>
      )
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/WorkerRegistrationApplication.jsx